import React, { useState } from 'react';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import InputField from '../../../../Components/Form/components/InputField';
import TextAreaField from '../../../../Components/Form/components/TextAreaField';
import { useForm } from 'react-hook-form';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useApiClient from '../../../Base/Hooks/useApiClient';
import apiClient from '../../../../Services/ApiClient';
import useTranslation from '../../../../Hooks/useTranslation';
import useNotification from '../../../../Hooks/useNotification.hook';
import useJob from '../../../Base/Hooks/useJob';
import PatientsGroupUpload from '../PatientsGroupsUpload/PatientsGroupUpload';

interface Props {
	onSuccess: () => void;
}

const PatientsGroupsCreateByImport: React.FC<Props> = ({ onSuccess }) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const { RenderJobWaiting, handleStartWaitingForJob } = useJob();

	const validationScheme = yup.object({
		name: yup
			.string()
			.required(t('validation.patientsGroupsCreateByImport.name')),
		description: yup.string().max(255).notRequired(),
		file: yup
			.mixed()
			.required(t('base.shared.fileIsNotPicked'))
			.test(
				'file-exists',
				t('base.shared.fileIsNotPicked') ?? '',
				value => value instanceof File,
			),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationScheme),
		defaultValues: {},
	});
	const { displayError } = useApiClient();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		try {
			const createdGroup = await apiClient.post(`/group`, {
				name: values.name,
				description: values.description,
			});

			if (createdGroup.status === 200 && values?.file) {
				const formData = new FormData();
				formData.append('file', values.file);

				const uploadResponse = await apiClient.postMultipart(
					`/group/${createdGroup.data.id}/member/import`,
					formData,
				);
				const jobId = uploadResponse.data;

				if (uploadResponse.status === 200 && jobId) {
					console.log('start waiting for job');
					handleStartWaitingForJob(jobId);
				} else {
					showNotificationAlert({
						content: 'component.groupMsgs.patientsImportFailed',
					});
				}
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleFileImportedSuccess = () => {
		showNotificationSuccess({
			content: t('component.groupMsgs.patientsImported'),
		});
		onSuccess();
	};

	const handleFileImportedFail = () => {
		showNotificationAlert({
			content: t('component.groupMsgs.patientsImportFailed'),
		});
	};

	return (
		<FormWrapper formMethods={formMethods}>
			<PatientsGroupUpload />
			<InputWrapper>
				<InputField
					name='name'
					label='base.groupName'
					placeholder='base.enterGroupName'
					isRequired
				/>
			</InputWrapper>
			<InputWrapper>
				<TextAreaField
					name='description'
					label='base.addGroupDescription'
					placeholder='base.writeGroupDescription'
					charactersLimit={255}
					showCharCounter
				/>
			</InputWrapper>
			<SubmitButton onSubmit={handleSubmit} disabled={isLoading} />
			<RenderJobWaiting
				onJobSuccess={handleFileImportedSuccess}
				onJobFailed={handleFileImportedFail}
			/>
		</FormWrapper>
	);
};

export default PatientsGroupsCreateByImport;
